// 侧边导航：PRD 6 大中心菜单（组 → 分段 → 叶子），默认折叠、当前路径自动展开
// 菜单配置数据见 @/menu（C1.1 六中心；动作类入口在总览页「新建」主按钮）
// 视觉对齐演示端：活动叶子左侧品牌色指示条 + 组展开平滑动画 + 组内分段小标题
import { Fragment, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { CreditCard, ChevronDown } from 'lucide-react';
import { MENU, type LeafItem } from '@/menu';
import { useReviewBadges, type ReviewBadges } from '@/hooks/useReviewBadges';
import { useDemoBadges, type DemoBadges } from '@/hooks/useDemoBadges';

/** 角标数据：审核类走 useReviewBadges，运营/风控/客服/充值等演示类走 useDemoBadges */
function useBadgesForPath(path: string): { review: ReviewBadges; demo: DemoBadges } {
  const review = useReviewBadges(path);
  const demo = useDemoBadges(path);
  return { review, demo };
}

/** 路由路径 → 动态角标映射（审核类 FIX-001/002/003 + 演示类 F-005/006/007） */
function dynamicBadgeFor(path: string, review: ReviewBadges, demo: DemoBadges): number | null {
  switch (path) {
    // 审核类
    case '/platform/finance/deposit-reviews':
      return review.depositPendingCount;
    case '/platform/finance/withdrawal-reviews':
      return review.withdrawalReviewableCount;
    case '/platform/account/audit/agent-onboarding':
      return review.agentOnboardingPendingCount;
    case '/platform/account/audit/merchant-onboarding':
      return review.merchantOnboardingPendingCount;
    // 演示类：运营/风控/客服/通道充值
    case '/platform/notifications':
      return demo.notificationDraftCount;
    // P2 菜单合并：风险预警壳基路径（alerts|gangs 两 Tab，主 Tab 挂角标）
    case '/platform/risk/alerts':
      return demo.riskAlertPendingCount;
    // P2 菜单合并：客户服务壳基路径（kb|tickets|email，工单 OPEN 计数挂壳）
    case '/platform/ops/service':
      return demo.ticketOpenCount;
    default:
      return null;
  }
}

/** 找到当前路径命中的叶子 → 返回其所在组标签（无则 null） */
function findActive(pathname: string): string | null {
  for (const group of MENU) {
    for (const leaf of group.leaves) {
      const matched =
        leaf.path === pathname ||
        (leaf.path !== '/platform' && pathname.startsWith(`${leaf.path}/`));
      if (matched) return group.label;
    }
  }
  return null;
}

function LeafLink({ leaf, dynamicBadge }: { leaf: LeafItem; dynamicBadge?: number | null }) {
  // 动态角标（审核类真实待审数）优先于静态配置；null（未加载/失败回退静态值）或 0 → 不显示
  const badge = dynamicBadge ?? leaf.badge;
  return (
    <NavLink
      key={leaf.path}
      to={leaf.path}
      end={leaf.path === '/platform'}
      className={({ isActive }) =>
        `relative w-full flex items-center justify-between pl-3 pr-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
          isActive
            ? 'bg-brand-50 text-brand-700'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* 活动指示条（演示端风格左侧竖线） */}
          {isActive && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 rounded-full bg-brand-600" />
          )}
          <span>{leaf.label}</span>
          {badge ? (
            <span className="min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] text-white font-medium flex items-center justify-center">
              {badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar() {
  const { pathname } = useLocation();

  // 角标真实化：审核类 + 演示类
  const badges = useBadgesForPath(pathname);

  // 初始全折叠，仅当前路径所在组展开（避免首帧闪烁）
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    const active = findActive(pathname);
    return new Set(MENU.map((g) => g.label).filter((l) => l !== active));
  });

  // 路由变化时自动展开目标所在组
  useEffect(() => {
    const active = findActive(pathname);
    if (active) {
      setCollapsedGroups((prev) => {
        if (!prev.has(active)) return prev;
        const next = new Set(prev);
        next.delete(active);
        return next;
      });
    }
  }, [pathname]);

  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <aside className="w-60 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0 z-30">
      {/* 品牌：Allinks · 平台管理后台（UI-R06 合规：禁用演示品牌名） */}
      <div className="px-5 py-5 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-gradient-to-br from-brand-500 to-brand-700 rounded-lg flex items-center justify-center flex-shrink-0 shadow-soft">
            <CreditCard className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-slate-900 font-bold text-sm leading-tight">Allinks</p>
            <p className="text-slate-400 text-xs">平台管理后台</p>
          </div>
        </div>
      </div>

      {/* 菜单 */}
      <nav className="flex-1 overflow-y-auto py-2 px-2.5">
        {MENU.map((item) => {
          const isCollapsed = collapsedGroups.has(item.label);
          return (
            <div key={item.label} className="mb-0.5">
              <button
                onClick={() => toggleGroup(item.label)}
                aria-expanded={!isCollapsed}
                className="w-full flex items-center justify-between px-2.5 py-2 text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                  {item.icon}
                  {item.label}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
                />
              </button>
              <div
                className={`grid transition-[grid-template-rows] duration-200 ease-spring ${
                  isCollapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
                }`}
              >
                <div className="overflow-hidden">
                  <div className="space-y-0.5 mt-0.5">
                    {item.leaves.map((leaf, idx) => {
                      // 组内分段：section 变化时插入分隔小标题（首项前不加额外间距）
                      const showSection =
                        leaf.section && (idx === 0 || item.leaves[idx - 1].section !== leaf.section);
                      return (
                        <Fragment key={leaf.path}>
                          {showSection && (
                            <p className="px-2.5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-300 select-none">
                              {leaf.section}
                            </p>
                          )}
                          <LeafLink leaf={leaf} dynamicBadge={dynamicBadgeFor(leaf.path, badges.review, badges.demo)} />
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <div className="px-4 py-3 border-t border-slate-100 text-[11px] text-slate-400">
        <p>Allinks Admin Console</p>
        <p>v0.5.0 · 分组菜单（组 → 段 → 项）</p>
      </div>
    </aside>
  );
}
